import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Newspaper, Clock, Search, 
  ExternalLink, Loader2, Languages, Cpu, BookOpen
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { translateBatch } from '../services/translationService';
import useSeo from '../hooks/useSeo';
import { breadcrumbJsonLd, SITE_URL } from '../lib/seoSchemas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsPage = () => {
  useSeo({
    title: 'Latest Government Policy & Scheme News — India | KnowMySchemes',
    description: 'Stay updated with the latest Indian government policy announcements, budget updates, scheme launches, and welfare-program news — refreshed throughout the day.',
    canonical: 'https://knowmyschemes.com/news',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Latest Government Policy & Scheme News — India',
        description:
          'Daily-refreshed news covering Indian government policy announcements, budget updates, scheme launches and welfare-program changes.',
        url: `${SITE_URL}/news`,
        isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'KnowMySchemes' },
        about: { '@type': 'Thing', name: 'Indian Government Policy & Welfare News' },
        inLanguage: 'en-IN',
      },
      breadcrumbJsonLd([
        { name: 'Home', url: `${SITE_URL}/` },
        { name: 'News', url: `${SITE_URL}/news` },
      ]),
    ],
  });
  const { t, language } = useLanguage();
  const [news, setNews] = useState([]);
  const [activeTab, setActiveTab] = useState('india'); // 'india' | 'tech'
  const [techIndiaCount, setTechIndiaCount] = useState(0);
  const [translatedNews, setTranslatedNews] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Track the current translation language to avoid race conditions
  const translationLangRef = useRef(language);
  const abortControllerRef = useRef(null);

  // Fetch news whenever the active tab changes
  useEffect(() => {
    fetchNews();
  }, [activeTab]);

  // Handle translation when language or news changes
  useEffect(() => {
    translationLangRef.current = language;
    
    // Cancel any pending translation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // If English, clear translations and return
    if (language === 'en') {
      setTranslatedNews({});
      setIsTranslating(false);
      return;
    }
    
    // Skip if no news
    if (news.length === 0) {
      return;
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const currentLang = language;
    
    const doTranslation = async () => {
      setIsTranslating(true);
      
      try {
        const headlines = news.map(n => n.headline || '');
        const summaries = news.map(n => n.summary || '');
        
        const [translatedHeadlines, translatedSummaries] = await Promise.all([
          translateBatch(headlines, currentLang, 'en'),
          translateBatch(summaries, currentLang, 'en')
        ]);
        
        // Check if language changed during translation
        if (translationLangRef.current !== currentLang) {
          console.log('Language changed during translation, discarding results');
          return;
        }
        
        const translations = {};
        news.forEach((item, index) => {
          translations[item.id || `news-${index}`] = {
            headline: translatedHeadlines[index] || headlines[index],
            summary: translatedSummaries[index] || summaries[index]
          };
        });
        
        setTranslatedNews(translations);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Translation error:', error);
        }
      } finally {
        // Only set isTranslating to false if this is still the current language
        if (translationLangRef.current === currentLang) {
          setIsTranslating(false);
        }
      }
    };
    
    doTranslation();
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [language, news]);

  const fetchNews = async () => {
    setLoading(true);
    setSearchQuery('');
    try {
      const endpoint = activeTab === 'tech' ? '/api/news-v2/tech' : '/api/news-v2';
      const reqLimit = activeTab === 'tech' ? 120 : 60;
      const url = `${API_URL}${endpoint}?limit=${reqLimit}`;

      const response = await axios.get(url);
      
      if (response.data.news && response.data.news.length > 0) {
        setNews(response.data.news);
        setLastUpdated(response.data.last_updated);
        if (typeof response.data.india_count === 'number') {
          setTechIndiaCount(response.data.india_count);
        }
      } else {
        setNews([]);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  // Get translated content - memoized function
  const getContent = useCallback((item, field, index) => {
    if (language === 'en') return item[field];
    
    const key = item.id || `news-${index}`;
    if (translatedNews[key] && translatedNews[key][field]) {
      return translatedNews[key][field];
    }
    return item[field];
  }, [language, translatedNews]);

  // Filter news by search
  const filteredNews = news.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (item.headline || '').toLowerCase().includes(query) ||
      (item.summary || '').toLowerCase().includes(query) ||
      (item.source || '').toLowerCase().includes(query)
    );
  });

  // Format time
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  // Format summary
  const formatSummary = (text) => {
    if (!text) return '';
    const MAX = 200;
    if (text.length <= MAX) return text;
    const slice = text.slice(0, MAX);
    // Prefer ending at the last complete sentence so the preview ends with a full stop.
    const lastSentence = Math.max(
      slice.lastIndexOf('. '),
      slice.lastIndexOf('! '),
      slice.lastIndexOf('? ')
    );
    if (lastSentence > 90) {
      return slice.slice(0, lastSentence + 1).trim();
    }
    // Otherwise cut at the last word and use a single clean ellipsis character.
    const lastSpace = slice.lastIndexOf(' ');
    return slice.slice(0, lastSpace > 0 ? lastSpace : MAX).trim() + '…';
  };

  // Reusable news/blog card
  const renderNewsCard = (item, index, testidPrefix = 'news-card') => (
    <Card
      key={item.id || `${testidPrefix}-${index}`}
      className="overflow-hidden bg-white hover:shadow-lg transition-shadow duration-300"
      data-testid={`${testidPrefix}-${index}`}
    >
      <div className="h-48 overflow-hidden bg-gray-200 relative">
        <img
          src={item.image_url || 'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=400&h=250&fit=crop'}
          alt={item.headline}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=400&h=250&fit=crop';
          }}
          loading="lazy"
        />
        {item.is_blog && (
          <span className="absolute top-3 left-3 bg-indigo-600 text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full">
            Blog
          </span>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-3 text-base">
          {getContent(item, 'headline', index)}
        </h3>

        <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">
          {formatSummary(getContent(item, 'summary', index))}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="font-medium">{item.source}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTimeAgo(item.published_at)}
            </span>
          </div>
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
            >
              {t('readMore') || 'Read More'}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </Card>
  );

  // For the Tech tab, separate curated blogs (TLDR) from regular tech news
  const blogItems = activeTab === 'tech' ? filteredNews.filter((i) => i.is_blog) : [];
  const mainItems = activeTab === 'tech' ? filteredNews.filter((i) => !i.is_blog) : filteredNews;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-3">
                {activeTab === 'tech' ? <Cpu className="w-8 h-8" /> : <Newspaper className="w-8 h-8" />}
                {activeTab === 'tech'
                  ? 'Tech News'
                  : (t('latestIndiaNews') || 'Latest India News')}
              </h1>
              {activeTab !== 'tech' && (
                <p className="text-blue-100">
                  {t('newsSubtitle') || 'Government schemes, policies & regional updates'}
                </p>
              )}
              {lastUpdated && (
                <p className="text-blue-200 text-sm mt-2 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {t('lastUpdated') || 'Last updated'}: {new Date(lastUpdated).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isTranslating && (
                <Badge className="bg-white/20 text-white">
                  <Languages className="w-3 h-3 mr-1 animate-spin" />
                  {t('translating') || 'Translating...'}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Section tabs: India News / Tech News */}
        <div className="flex items-center gap-2 mb-5" data-testid="news-section-tabs">
          <button
            onClick={() => setActiveTab('india')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition border ${
              activeTab === 'india'
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-india-news"
          >
            <Newspaper className="w-4 h-4" />
            India News
          </button>
          <button
            onClick={() => setActiveTab('tech')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition border ${
              activeTab === 'tech'
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-tech-news"
          >
            <Cpu className="w-4 h-4" />
            Tech News
            {activeTab === 'tech' && techIndiaCount > 0 && (
              <span className="ml-1 text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">
                {techIndiaCount} 🇮🇳
              </span>
            )}
          </button>
        </div>

        {/* Search Row */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder={t('searchNews') || "Search news..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
        </div>

        {/* News Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">{t('loadingNews') || 'Loading news...'}</span>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('noNewsFound') || 'No News Found'}</h3>
            <p className="text-gray-500">
              {searchQuery ? t('tryDifferentSearch') || 'Try a different search term' : t('newsUpdatedSoon') || 'News will be updated soon'}
            </p>
          </div>
        ) : (
          <>
            {/* Blogs section — curated TLDR.tech blogs (Tech tab only) */}
            {activeTab === 'tech' && blogItems.length > 0 && (
              <div className="mb-10" data-testid="blogs-section">
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-gray-900">Blogs</h2>
                  <span className="text-xs text-gray-500 font-medium">({blogItems.length})</span>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Hand-picked engineering & founder essays from across the web
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blogs-grid">
                  {blogItems.map((item, index) => renderNewsCard(item, index, 'blog-card'))}
                </div>
              </div>
            )}

            {/* Main news grid */}
            {mainItems.length > 0 && (
              <>
                {activeTab === 'tech' && blogItems.length > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    <Newspaper className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-bold text-gray-900">Latest Tech News</h2>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
                  {mainItems.map((item, index) => renderNewsCard(item, index, 'news-card'))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default NewsPage;
